// =====================================================================
// src/lib/disponibilidade.js — quais quartos cobrem um período, sem DOM
//
// Cópia de `buscarCombinacoes` e `_datasOcupadasFlat` do Monteiro de
// Moraes (index.html, 5571 e 5636). A tela (`src/modulos/reservas.js`, aba
// Disponibilidade) copia `renderDisponibilidade` (5727) e
// `_dispoRenderCombo` (5961).
//
// A pergunta: "tenho hóspede de 3 a 7 — onde ele cabe?" Primeiro os quartos
// livres o período INTEIRO ("sem troca"); se nenhum cobre, combinações em
// que o hóspede muda de quarto no meio ("com troca"), até 4 trocas, com um
// limite de tempo para não travar a tela.
//
// Divergências de domínio, todas obrigatórias:
//   · sem VALOR. Lá cada opção sai com diária × dias; aqui o hotel não tem
//     diária por quarto e nenhum perfil de hotel vê valor (INVARIANTE 2).
//     A ordem, que lá era "mais barato primeiro", aqui é por quarto;
//   · sem pré-reserva pendente nem WhatsApp (Hugo, 21/09: "esqueça
//     qualquer função de WhatsApp no momento"). O resultado abre a modal
//     de lançar reserva que já existe;
//   · 456 quartos, não 28: os livres saem AGRUPADOS POR ANDAR, senão a
//     lista "sem troca" teria centenas de linhas; e a busca com troca só
//     roda quando nenhum quarto cobre o período — com centenas de quartos
//     livres ela é ruído, não informação;
//   · lá conta `confirmada` e `pendente`; aqui `situacao` — cancelada e
//     no-show liberam o quarto, `concluida` não ocupa data futura.
// =====================================================================
import { somarDias, noites, quartosParaReserva, fimDaOcupacao } from './reservas.js';

// Os dias de [de, ate): check-in inclusive, check-out exclusive — a noite
// do check-out já é de quem chega.
export function diasEntre(de, ate) {
  const dias = [];
  for (let d = de; d < ate; d = somarDias(d, 1)) dias.push(d);
  return dias;
}

// Uma reserva ocupa o quarto? Cancelada e no-show, não.
export function ocupa(r) {
  return !!r && r.situacao !== 'cancelada' && r.situacao !== 'noshow' && !!r.check_in && !!r.check_out;
}

// `_datasOcupadasFlat` (5636): o conjunto de dias ocupados de um quarto
// dentro de [inicio, fim).
export function datasOcupadas(reservas, unidadeId, inicio, fim) {
  const oc = new Set();
  for (const r of reservas || []) {
    if (!ocupa(r) || r.unidade_id !== unidadeId) continue;
    const saida = fimDaOcupacao(r);   // 0096: a concluída antes do previsto libera o resto
    if (saida <= inicio || r.check_in >= fim) continue;   // não cruza
    const de = r.check_in < inicio ? inicio : r.check_in;
    const ate = saida > fim ? fim : saida;
    for (const d of diasEntre(de, ate)) oc.add(d);
  }
  return oc;
}

// O período pedido faz sentido? A mesma regra do MdM (`executarPesquisa
// Disponibilidade`, 5915), com a frase dele.
export function validarPeriodo({ inicio, fim }, hoje) {
  if (!inicio || !fim) return ['Preencha check-in e check-out.'];
  if (fim <= inicio) return ['Check-out deve ser depois do check-in.'];
  if (hoje && inicio < hoje) return ['Check-in no passado — confira a data.'];
  return [];
}

// Os quartos livres o período inteiro — o "scan exaustivo" do MdM (5648),
// sem o valor. Devolve na ordem das unidades (por número).
export function quartosLivres(unidades, reservas, inicio, fim) {
  const dias = diasEntre(inicio, fim);
  return (unidades || []).filter(u => u.ativo !== false).filter(u => {
    const oc = datasOcupadas(reservas, u.id, inicio, fim);
    return dias.every(d => !oc.has(d));
  });
}

// Por andar, para a tela. É o MESMO agrupamento do seletor de quarto da
// modal (`quartosParaReserva`): "Sem andar" no fim, quartos em ordem
// numérica. Uma segunda versão aqui seria a primeira a divergir (§4.2).
export function agruparPorAndar(quartos) { return quartosParaReserva(quartos); }

// `buscarCombinacoes` (5571), só a parte com troca: segmentos
// [{ unidade_id, numero, check_in, check_out, dias }] que, encadeados,
// cobrem [inicio, fim). Sem valor. Mesmos limites dele: 4 segmentos, 50
// resultados, 100 000 iterações, 5 s (`agora` e `limiteMs` são injetáveis
// para o teste não depender do relógio).
export function buscarComTroca(unidades, reservas, inicio, fim, opts = {}) {
  const maxSegmentos = opts.maxSegmentos || 4;
  const maxResultados = opts.maxResultados || 50;
  const limiteMs = opts.limiteMs ?? 5000;
  const maxIter = opts.maxIter || 100000;
  const agora = opts.agora || (() => Date.now());
  const t0 = agora();
  let iters = 0, abortado = false;
  const quartos = (unidades || []).filter(u => u.ativo !== false);
  const ocupadas = {};
  for (const q of quartos) ocupadas[q.id] = datasOcupadas(reservas, q.id, inicio, fim);
  const resultados = [];

  function backtrack(atual, segmentos) {
    if (abortado) return;
    iters++;
    if (iters > maxIter) { abortado = true; return; }
    if ((iters & 0xff) === 0 && agora() - t0 > limiteMs) { abortado = true; return; }
    if (resultados.length >= maxResultados) return;
    if (atual >= fim) { resultados.push(segmentos.map(s => ({ ...s }))); return; }
    if (segmentos.length >= maxSegmentos) return;
    for (const q of quartos) {
      if (abortado) return;
      // não reusa o mesmo quarto na combinação (1016 → 1032 → 1016 é péssimo)
      if (segmentos.some(s => s.unidade_id === q.id)) continue;
      if (ocupadas[q.id].has(atual)) continue;
      // estende enquanto livre, até o fim
      let fimSeg = atual, guarda = 0;
      while (fimSeg < fim && !ocupadas[q.id].has(fimSeg) && guarda++ < 400) fimSeg = somarDias(fimSeg, 1);
      if (fimSeg <= atual) continue;
      const dias = noites(atual, fimSeg);
      // tenta também a metade, para explorar combinações
      const fins = [fimSeg];
      if (dias >= 4) fins.push(somarDias(atual, Math.ceil(dias / 2)));
      for (const f2 of fins) {
        if (abortado) return;
        const d2 = noites(atual, f2);
        if (d2 < 1) continue;
        segmentos.push({ unidade_id: q.id, numero: q.numero, check_in: atual, check_out: f2, dias: d2 });
        backtrack(f2, segmentos);
        segmentos.pop();
        if (abortado) return;
      }
    }
  }
  try { backtrack(inicio, []); } catch (e) { abortado = true; }

  // só as com troca; menos trocas primeiro, depois pelo primeiro quarto;
  // sem duplicata (mesma sequência de quartos e datas)
  const chave = c => c.map(s => `${s.unidade_id}:${s.check_in}:${s.check_out}`).join('|');
  const vistas = new Set(); const unicos = [];
  for (const c of resultados.filter(r => r.length > 1)
    .sort((a, b) => a.length - b.length || String(a[0].numero).localeCompare(String(b[0].numero), undefined, { numeric: true }))) {
    const k = chave(c); if (vistas.has(k)) continue; vistas.add(k); unicos.push(c);
  }
  return { combinacoes: unicos.slice(0, maxResultados), abortado, iteracoes: iters };
}

// O resultado inteiro da pesquisa, do jeito que a tela desenha: os livres
// por andar e, SÓ se não houver nenhum, as combinações com troca.
// 0067: quarto bloqueado (Indisponível/Manutenção) não é oferecido — o
// banco recusa reserva nele. O MdM oferece o flat bloqueado aqui; a
// divergência é consequência da decisão do Hugo (26/09): o bloqueio IMPEDE
// reserva nova.
// 0103: quarto SEM IMOBILIÁRIA também não é oferecido — o banco recusa
// reserva nele (Hugo, 03/11: "a reserva é da imobiliária do quarto"; o
// quarto sem ela é cadastro incompleto). `semImobiliaria` conta os que
// ficaram de fora (ativos, não bloqueados), para a tela dizer quantos.
export function pesquisar(unidades, reservas, inicio, fim, opts = {}) {
  const abertos = (unidades || []).filter(u => !u.bloqueio);
  const semImobiliaria = abertos.filter(u => u.ativo !== false && !u.imobiliaria_id).length;
  unidades = abertos.filter(u => u.imobiliaria_id);
  const livres = quartosLivres(unidades, reservas, inicio, fim);
  const diarias = noites(inicio, fim);
  if (livres.length) return { diarias, livres, porAndar: agruparPorAndar(livres), comTroca: [], abortado: false, semImobiliaria };
  const b = buscarComTroca(unidades, reservas, inicio, fim, opts);
  return { diarias, livres: [], porAndar: [], comTroca: b.combinacoes, abortado: b.abortado, semImobiliaria };
}

// "21/09→24/09, 3d" — a pílula de segmento de `_dispoRenderCombo` (5968)
export function rotuloDoSegmento(s) {
  const dm = iso => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  return `Quarto ${s.numero} (${dm(s.check_in)}→${dm(s.check_out)}, ${s.dias}d)`;
}
export function rotuloDeTrocas(n) { return n === 0 ? '✨ Sem trocas' : `${n} troca(s) de quarto`; }
